import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Pencil, Settings } from "lucide-react";
import { TAB_LIST, TAB_TRIGGER } from "@/lib/tabStyles";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errorMessage } from "@/lib/api";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { CompanyDataSection } from "@/components/profile/CompanyDataTab";
import { AssessmentSection } from "@/components/profile/AssessmentTab";
import { PrivacyTab } from "@/components/profile/PrivacyTab";
import { Growth } from "@/lib/growth";

const TABS = [
    { value: "profilo", label: "Profilo" },
    { value: "privacy", label: "Privacy e consensi" },
];
const LEGACY = { dati: "sezione-dati", assessment: "sezione-assessment" };

const SpinLine = ({ spin }) => (
    <p className="mt-3 text-edi-muted [overflow-wrap:anywhere]" data-testid="profile-spin-line">
        Sportello di riferimento: <strong className="text-edi-on-graphite" data-testid="profile-spin-name">{spin.name}</strong>
        {spin.address && <span data-testid="profile-spin-address"> · {spin.address}, {spin.cap} {spin.city}</span>}
        {(spin.phone || spin.email) && (
            <span className="block mt-1" data-testid="profile-spin-contacts">
                {spin.phone && <>Tel. <a href={`tel:${spin.phone.replace(/\s/g, "")}`} className="underline" data-testid="profile-spin-phone">{spin.phone}</a></>}
                {spin.phone && spin.email && " · "}
                {spin.email && <a href={`mailto:${spin.email}`} className="underline" data-testid="profile-spin-email">{spin.email}</a>}
            </span>
        )}
    </p>
);

const ScoreCard = ({ latest, history }) => (
    <div className="edi-on-light edi-glow-soft rounded-xl bg-edi-surface border border-edi-border px-5 py-4" data-testid="profile-header-score">
        <p className="text-sm text-edi-muted">Ultimo punteggio</p>
        <p className="mt-1 flex items-center gap-3">
            <span className="font-heading text-4xl font-bold">{latest.score_total}<span className="text-lg text-edi-muted">/100</span></span>
            <span className="edi-level-badge" data-testid="profile-header-level">{latest.level}</span>
        </p>
        {history?.length >= 2 && (
            <p className="mt-2 text-sm" data-testid="profile-header-growth">
                <Growth current={latest.score_total} reference={history[1].score_total} suffix="rispetto al precedente" />
            </p>
        )}
    </div>
);

const HeaderBand = ({ company, latest, history, editing, onEdit }) => (
    <div className="surface-dark rounded-xl relative px-5 py-6 sm:px-8 sm:py-8 flex flex-wrap items-stretch justify-between gap-6" data-testid="profile-header-band">
        {!editing && (
            <button type="button" onClick={onEdit} aria-label="Modifica profilo" data-testid="company-edit-gear"
                className="lg:hidden absolute top-2 right-2 inline-flex h-11 w-11 items-center justify-center rounded-lg text-edi-on-graphite hover:bg-white/10">
                <Settings aria-hidden="true" size={22} />
            </button>
        )}
        <div className="min-w-0 flex-1 basis-[28rem] pr-12 lg:pr-0">
            <p className="edi-eyebrow">Profilo aziendale</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold break-words" data-testid="profile-company-name">{company.profile.ragione_sociale}</h1>
            {company.spin && <SpinLine spin={company.spin} />}
        </div>
        <div className="flex flex-col items-start lg:items-end justify-between gap-4">
            {latest && <ScoreCard latest={latest} history={history} />}
            {!editing && (
                <button type="button" onClick={onEdit} data-testid="company-edit-button"
                    className="hidden lg:inline-flex items-center gap-2 bg-transparent p-0 font-semibold text-edi-on-graphite underline underline-offset-4 decoration-2 hover:decoration-edi-lime rounded-sm">
                    <Pencil aria-hidden="true" size={18} /> Modifica profilo
                </button>
            )}
        </div>
    </div>
);

export default function Profile() {
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const [scrollTarget] = useState(() => LEGACY[params.get("tab")] || null);
    const tab = params.get("tab") === "privacy" ? "privacy" : "profilo";
    const [company, setCompany] = useState(null);
    const [latest, setLatest] = useState(null);
    const [history, setHistory] = useState([]);
    const [editing, setEditing] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/companies/me").then(({ data }) => setCompany(data)).catch((e) => setError(errorMessage(e)));
    }, []);
    useEffect(() => {
        if (LEGACY[params.get("tab")]) setParams({}, { replace: true, state: location.state });
    }, [params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (company) document.title = `Profilo – ${company.profile.ragione_sociale} | EDI Assessment AI`;
    }, [company]);

    const goTab = (value) => setParams(value === "privacy" ? { tab: "privacy" } : {});

    if (error) return <div className="mx-auto max-w-6xl px-4 py-12"><Notice kind="error" role="alert">{error}</Notice></div>;
    if (!company) return <Loading />;

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
            <HeaderBand company={company} latest={latest} history={history} editing={editing} onEdit={() => { goTab("profilo"); setEditing(true); }} />
            {location.state?.registered && <div className="mt-4"><Notice kind="success" testId="register-success-message">Registrazione completata: il tuo account è attivo.</Notice></div>}
            <Tabs value={tab} onValueChange={goTab} className="mt-6">
                <TabsList className={`${TAB_LIST} flex-wrap`}>
                    {TABS.map((t) => (
                        <TabsTrigger key={t.value} value={t.value} data-testid={`profile-tab-${t.value}`}
                            className={`${TAB_TRIGGER} flex-1 sm:flex-none`}>
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                <TabsContent value="profilo" className="mt-8 space-y-14 focus-visible:ring-0">
                    <CompanyDataSection company={company} onSaved={setCompany} scrollHere={scrollTarget === "sezione-dati"} editing={editing} setEditing={setEditing} />
                    <AssessmentSection goTab={goTab} justSubmitted={location.state?.submitted} onLatest={(l, items) => { setLatest(l); setHistory(items || []); }} scrollHere={scrollTarget === "sezione-assessment"} />
                </TabsContent>
                <TabsContent value="privacy" className="mt-8 focus-visible:ring-0"><PrivacyTab company={company} onChange={setCompany} /></TabsContent>
            </Tabs>
        </div>
    );
}
